import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { api, session } from "./api.js";
import { loadContent, useContent } from "./cms/store.js";
import { installUnsavedGuard } from "./unsavedGuard.js";
import { upsertJsonLd, SITE_URL } from "./seo.js";
import { getConsent, loadAnalyticsScripts } from "./consent.js";
import { reportGadsPurchase } from "./tracking.js";
import { captureAttribution } from "./attribution.js";
import { funnel, notePage } from "./funnel.js";

import Header from "./components/Header.jsx";
import Footer from "./components/Footer.jsx";
import LegalReacceptGate from "./components/LegalReacceptGate.jsx";
import CookieBanner from "./components/CookieBanner.jsx";
import LandingRoute, { useActiveLanding } from "./landing/Landing.jsx";
import { ScrollToTop, ToastHost } from "./components/ui.jsx";

/* Strony ładowane na żądanie — osobne paczki JS (patrz <Suspense> przy <Routes>). */
const QuickCaseFlag = lazy(() => import("./components/QuickCaseFlag.jsx"));
const ChatWidget = lazy(() => import("./components/ChatWidget.jsx"));
const ChatInbox = lazy(() => import("./pages/ChatInbox.jsx"));
const Home = lazy(() => import("./pages/site/Home.jsx"));
const About = lazy(() => import("./pages/site/About.jsx"));
const HowItWorks = lazy(() => import("./pages/site/HowItWorks.jsx"));
const Pricing = lazy(() => import("./pages/site/Pricing.jsx"));
const Knowledge = lazy(() => import("./pages/site/Knowledge.jsx"));
const Article = lazy(() => import("./pages/site/Article.jsx"));
const Faq = lazy(() => import("./pages/site/Faq.jsx"));
const Contact = lazy(() => import("./pages/site/Contact.jsx"));
const LegalPage = lazy(() => import("./pages/site/LegalPage.jsx"));
const NotFound = lazy(() => import("./pages/site/NotFound.jsx"));
const Login = lazy(() => import("./pages/Login.jsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.jsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.jsx"));
const DocumentDecline = lazy(() => import("./pages/DocumentDecline.jsx"));
const Account = lazy(() => import("./pages/Account.jsx"));
const ClientPanel = lazy(() => import("./pages/ClientPanel.jsx"));
const NewCase = lazy(() => import("./pages/NewCase.jsx"));
const CaseDetail = lazy(() => import("./pages/CaseDetail.jsx"));
const LawyerPanel = lazy(() => import("./pages/LawyerPanel.jsx"));
const LawyerReview = lazy(() => import("./pages/LawyerReview.jsx"));
const LawyerArchive = lazy(() => import("./pages/LawyerArchive.jsx"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout.jsx"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard.jsx"));
const PostsList = lazy(() => import("./pages/admin/Posts.jsx").then((m) => ({ default: m.PostsList })));
const PostEdit = lazy(() => import("./pages/admin/Posts.jsx").then((m) => ({ default: m.PostEdit })));
const FaqAdmin = lazy(() => import("./pages/admin/Collections.jsx").then((m) => ({ default: m.FaqAdmin })));
const TeamAdmin = lazy(() => import("./pages/admin/Collections.jsx").then((m) => ({ default: m.TeamAdmin })));
const LegalAdmin = lazy(() => import("./pages/admin/Collections.jsx").then((m) => ({ default: m.LegalAdmin })));
const LandingsAdmin = lazy(() => import("./pages/admin/Collections.jsx").then((m) => ({ default: m.LandingsAdmin })));
const LandingSharedAdmin = lazy(() => import("./pages/admin/Collections.jsx").then((m) => ({ default: m.LandingSharedAdmin })));
const GoogleReviewsAdmin = lazy(() => import("./pages/admin/Collections.jsx").then((m) => ({ default: m.GoogleReviewsAdmin })));
const CasesOverview = lazy(() => import("./pages/admin/CasesOverview.jsx"));
const Statistics = lazy(() => import("./pages/admin/Statistics.jsx"));
const CaseFlagsAdmin = lazy(() => import("./pages/admin/CaseFlagsAdmin.jsx"));
const LawyersAdmin = lazy(() => import("./pages/admin/LawyersAdmin.jsx"));
const AdminsAdmin = lazy(() => import("./pages/admin/AdminsAdmin.jsx"));
const ClientsAdmin = lazy(() => import("./pages/admin/ClientsAdmin.jsx"));
const CustomOrders = lazy(() => import("./pages/admin/CustomOrders.jsx"));
const DiscountCodes = lazy(() => import("./pages/admin/DiscountCodes.jsx"));
const FileLog = lazy(() => import("./pages/admin/FileLog.jsx"));
const Funnel = lazy(() => import("./pages/admin/Funnel.jsx"));
const CustomOrder = lazy(() => import("./pages/CustomOrder.jsx"));
const ServicesAdmin = lazy(() => import("./pages/admin/ServicesAdmin.jsx"));
const EmailTemplatesAdmin = lazy(() => import("./pages/admin/EmailTemplatesAdmin.jsx"));
const Settings = lazy(() => import("./pages/admin/Settings.jsx"));
const HomeEditor = lazy(() => import("./pages/admin/ContentPages.jsx").then((m) => ({ default: m.HomeEditor })));
const AboutEditor = lazy(() => import("./pages/admin/ContentPages.jsx").then((m) => ({ default: m.AboutEditor })));
const HowEditor = lazy(() => import("./pages/admin/ContentPages.jsx").then((m) => ({ default: m.HowEditor })));
const PricingEditor = lazy(() => import("./pages/admin/ContentPages.jsx").then((m) => ({ default: m.PricingEditor })));
const ContactEditor = lazy(() => import("./pages/admin/ContentPages.jsx").then((m) => ({ default: m.ContactEditor })));

/* --------------------------------------------------------- serwis publiczny */

/* ----------------------------------------------------------- część zalogowana */

/* ----------------------------------------------------------------- panel CMS */

/*
 * Role dopuszczone do panelu treści.
 *
 * To tylko ukrycie interfejsu — realną kontrolę pełni backend, który przy
 * każdym zapisie treści sprawdza rolę "admin" (patrz prawly_server/content.js).
 * Nadanie uprawnień: UPDATE users SET role='admin' WHERE email='…';
 *
 * Do pracy nad wyglądem panelu bez konta administratora można poszerzyć listę
 * zmienną środowiskową — działa to wyłącznie przy sterowniku "local", bo API
 * i tak odrzuci zapis od konta bez roli admin:
 *   REACT_APP_CMS_DRIVER=local REACT_APP_ADMIN_ROLES=admin,lawyer npm start
 */
const ADMIN_ROLES = (process.env.REACT_APP_ADMIN_ROLES || "admin")
  .split(",")
  .map((r) => r.trim())
  .filter(Boolean);

/* Dane strukturalne firmy (Organization/LegalService), takie same na każdej
   stronie — osobno od useSeo, który per-stronowo nadpisuje tylko blok
   "seo-page". Adres w CMS jest jednym polem tekstowym ("ul. X, 00-000
   Miasto"), więc rozbijamy go na przecinku — przy niepasującym formacie po
   prostu wpisujemy całość jako streetAddress zamiast się wywracać. */
function parseAddress(raw) {
  const [street, rest] = String(raw || "").split(",").map((s) => s.trim());
  const m = rest?.match(/^(\d{2}-\d{3})\s+(.+)$/);
  return m
    ? { streetAddress: street, postalCode: m[1], addressLocality: m[2] }
    : { streetAddress: raw || undefined };
}

function SiteSchema() {
  const s = useContent("settings");

  useEffect(() => {
    if (!s.brand) return;
    upsertJsonLd("seo-org", {
      "@context": "https://schema.org",
      "@type": "LegalService",
      name: s.brand,
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png`,
      image: `${SITE_URL}/hero-lawyer.jpg`,
      description: s.claim,
      email: s.email,
      telephone: s.phone,
      address: { "@type": "PostalAddress", addressCountry: "PL", ...parseAddress(s.address) },
      sameAs: [s.socials?.linkedin, s.socials?.facebook, s.socials?.x].filter(Boolean),
    });
  }, [s.brand, s.claim, s.email, s.phone, s.address, s.socials]);

  return null;
}

/* /cennik: szybka, prerenderowana strona docelowa (landing z layout "cennik"); gdy ją wyłączono w /admin/landingi — zwykła podstrona cennika. */
function PricingRoute() {
  const landing = useActiveLanding("/cennik");
  return landing ? <LandingRoute slug="cennik" /> : <Pricing />;
}

function Guard({ role, roles, children }) {
  const s = session.get();
  if (!s?.user) return <Navigate to="/logowanie" replace />;
  if (role && s.user.role !== role) return <Navigate to="/" replace />;
  if (roles && !roles.includes(s.user.role)) return <Navigate to="/" replace />;
  return children;
}

let conversionsChecked = false;

/* Strony prywatne (konto, panel, administracja) — nie liczymy ich jako odsłon serwisu w lejku. */
const PRIVATE_PATH = /^\/(panel|admin|prawnik|zamowienie|logowanie|zapomnialem-hasla|resetuj-haslo)(\/|$)/;

export default function App() {
  const [, force] = useState(0);
  const [legalGateActive, setLegalGateActive] = useState(false);
  /* Strona docelowa z reklamy ma własny, lekki układ (landing/Landing.jsx) —
     bez globalnego nagłówka, stopki, czatu (odpytuje API co 30 s) i chorągiewki. */
  const { pathname } = useLocation();
  const onLanding = !!useActiveLanding(pathname);

  /* Lejek zamówień dla całego serwisu: odsłona każdej publicznej podstrony (landingi mają własne lp_view) oraz
     kliknięcia linków do zamówienia — z którego miejsca strony (nagłówek, stopka, treść). Przyciski bez adresu
     (karty usług, chorągiewka) zgłaszają się same (hooks.js, QuickCaseFlag). */
  useEffect(() => {
    if (PRIVATE_PATH.test(pathname)) return;
    notePage(pathname);
    if (!onLanding) funnel("page_view");
  }, [pathname, onLanding]);

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target?.closest?.('a[href^="/panel/nowa"]');
      if (!a || a.hasAttribute("data-kind")) return; // karty cennika na landingu zgłasza Landing.jsx
      funnel("site_cta", { where: a.closest("header") ? "header" : a.closest("footer") ? "footer" : "page" });
    };
    // faza przechwytywania: zdarzenie ma wyjść z adresem strony, z której kliknięto, zanim router przejdzie do zamówienia
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  /* Treść serwisu pobieramy raz przy starcie; do pierwszego renderu służy
     ostatnia zapisana odpowiedź serwera (patrz cms/store.js). */
  useEffect(() => {
    loadContent();
    installUnsavedGuard();
    // Zgoda na analitykę udzielona wcześniej (poprzednia wizyta) — wracający
    // użytkownik nie musi klikać banera ponownie.
    if (getConsent() === true) loadAnalyticsScripts();
    // Parametry reklamowe w adresie (utm_*, gclid, ttclid, fbclid) — tylko
    // odczyt i lokalny zapis, bez ciasteczka i bez zgody z bannera (patrz
    // attribution.js). Widoczne administratorowi przy zamówieniu.
    captureAttribution();
  }, []);

  /* Dosyłka konwersji Google Ads: klient, który zapłacił, ale nie wrócił na stronę sprawy
     (zamknięta karta po płatności, inne urządzenie), zostaje zaliczony przy pierwszej
     kolejnej wizycie na dowolnej stronie. Już zgłoszone płatności są pomijane (flaga +
     transaction_id), więc to nie dubluje konwersji. */
  useEffect(() => {
    // ErrorBoundary w index.js montuje App od nowa przy każdej zmianie adresu — zapytanie wysyłamy raz na wczytanie strony
    if (conversionsChecked || session.get()?.user?.role !== "client") return;
    conversionsChecked = true;
    api("/me/conversions")
      .then((r) => (r.payments || []).forEach((p) => reportGadsPurchase(p, p.ads)))
      .catch(() => {});
  }, []);

  /* Gdy token wygaśnie w trakcie pracy, api.js czyści sesję i wysyła to
     zdarzenie — przerysowujemy nawigację, żeby nie pokazywała zalogowanego. */
  useEffect(() => {
    const onExpired = () => force((x) => x + 1);
    window.addEventListener("prawly:session-expired", onExpired);
    return () => window.removeEventListener("prawly:session-expired", onExpired);
  }, []);

  /* Po zmianie sesji odświeżamy treść — administrator dostaje wtedy również
     wpisy nieopublikowane, a po wylogowaniu znikają one z pamięci. */
  const refresh = () => {
    force((x) => x + 1);
    loadContent({ quiet: true });
  };

  const logout = () => {
    session.clear();
    refresh();
  };

  return (
    <ToastHost>
      <ScrollToTop />
      <SiteSchema />
      <LegalReacceptGate onActiveChange={setLegalGateActive} />
      {/* `inert`, gdy bramka ponownej akceptacji jest aktywna — bez tego dało
          się dotrzeć klawiaturą (Tab) do przycisków pod nakładką, mimo że
          wizualnie zasłonięte i niedostępne dla myszy. */}
      <div inert={legalGateActive}>
        <CookieBanner />
        {!onLanding && <Suspense fallback={null}><QuickCaseFlag /></Suspense>}
        {!onLanding && <Suspense fallback={null}><ChatWidget /></Suspense>}
        {!onLanding && <Header onLogout={logout} />}

        {/* Strony ładowane na żądanie (osobne paczki JS) — landing z reklam nie pobiera panelu
            prawnika/admina ani reszty serwisu. Szkielet trzyma wysokość, żeby stopka nie skakała. */}
        <Suspense fallback={<div style={{ minHeight: "100vh" }} aria-hidden="true" />}>
        <Routes>
        {/* ---------------------------------------------------- strony publiczne */}
        <Route path="/" element={<Home />} />
        <Route path="/o-nas" element={<About />} />
        <Route path="/jak-to-dziala" element={<HowItWorks />} />
        <Route path="/cennik" element={<PricingRoute />} />
        <Route path="/baza-wiedzy" element={<Knowledge />} />
        <Route path="/baza-wiedzy/:slug" element={<Article />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/kontakt" element={<Contact />} />
        <Route path="/regulamin" element={<LegalPage which="regulamin" />} />
        <Route path="/polityka-prywatnosci" element={<LegalPage which="prywatnosc" />} />

        {/* przekierowania ze starych adresów */}
        <Route path="/oferta" element={<Navigate to="/cennik" replace />} />
        <Route path="/blog" element={<Navigate to="/baza-wiedzy" replace />} />

        {/* --------------------------------------------------------- konto klienta */}
        <Route path="/logowanie" element={<Login onAuth={refresh} />} />
        <Route path="/zapomnialem-hasla" element={<ForgotPassword />} />
        <Route path="/resetuj-haslo" element={<ResetPassword />} />
        <Route path="/rezygnacja-z-dokumentu" element={<DocumentDecline />} />
        <Route
          path="/moje-konto"
          element={
            <Guard>
              <Account />
            </Guard>
          }
        />
        <Route
          path="/panel"
          element={
            <Guard role="client">
              <ClientPanel />
            </Guard>
          }
        />
        {/* Bez Guard — dostępna też bez sesji: NewCase.jsx sam obsługuje
            logowanie/rejestrację inline przy składaniu zamówienia i sam
            blokuje dostęp zalogowanemu prawnikowi/adminowi. */}
        <Route path="/panel/nowa" element={<NewCase />} />
        {/* Zamówienie indywidualne z linku w mailu — sama strona odsyła do logowania i wraca. */}
        <Route path="/zamowienie/:id" element={<CustomOrder />} />
        <Route
          path="/sprawa/:id"
          element={
            <Guard>
              <CaseDetail />
            </Guard>
          }
        />

        {/* ------------------------------------------------------------ prawnik */}
        <Route
          path="/prawnik"
          element={
            <Guard role="lawyer">
              <LawyerPanel />
            </Guard>
          }
        />
        <Route
          path="/prawnik/sprawa/:id"
          element={
            // Też administrator — z panelu „Wszystkie sprawy” (CasesOverview)
            // można otworzyć podgląd dowolnej sprawy; backend już i tak
            // dopuszcza admina do GET /api/cases/:id.
            <Guard roles={['lawyer', 'admin']}>
              <LawyerReview />
            </Guard>
          }
        />
        <Route
          path="/prawnik/archiwum"
          element={
            <Guard role="lawyer">
              <LawyerArchive />
            </Guard>
          }
        />
        <Route
          path="/prawnik/czat"
          element={
            <Guard roles={['lawyer', 'admin']}>
              <main className="container" style={{ maxWidth: 1300 }}>
                <ChatInbox />
              </main>
            </Guard>
          }
        />

        {/* ---------------------------------------------------------- panel CMS */}
        <Route
          path="/admin"
          element={
            <Guard roles={ADMIN_ROLES}>
              <AdminLayout />
            </Guard>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="strona-glowna" element={<HomeEditor />} />
          <Route path="o-nas" element={<AboutEditor />} />
          <Route path="jak-to-dziala" element={<HowEditor />} />
          <Route path="cennik" element={<PricingEditor />} />
          <Route path="kontakt" element={<ContactEditor />} />
          <Route path="wpisy" element={<PostsList />} />
          <Route path="wpisy/:id" element={<PostEdit />} />
          <Route path="faq" element={<FaqAdmin />} />
          <Route path="landingi" element={<LandingsAdmin />} />
          <Route path="landingi-wspolne" element={<LandingSharedAdmin />} />
          <Route path="opinie-google" element={<GoogleReviewsAdmin />} />
          <Route path="zespol" element={<TeamAdmin />} />
          <Route path="dokumenty" element={<LegalAdmin />} />
          <Route path="sprawy" element={<CasesOverview />} />
          <Route path="statystyki" element={<Statistics />} />
          <Route path="flagi-spraw" element={<CaseFlagsAdmin />} />
          <Route path="prawnicy" element={<LawyersAdmin />} />
          <Route path="administratorzy" element={<AdminsAdmin />} />
          <Route path="klienci" element={<ClientsAdmin />} />
          <Route path="czat" element={<ChatInbox />} />
          <Route path="zamowienia-indywidualne" element={<CustomOrders />} />
          <Route path="kody-rabatowe" element={<DiscountCodes />} />
          <Route path="rejestr-plikow" element={<FileLog />} />
          <Route path="lejek" element={<Funnel />} />
          <Route path="uslugi" element={<ServicesAdmin />} />
          <Route path="e-maile" element={<EmailTemplatesAdmin />} />
          <Route path="ustawienia" element={<Settings />} />
        </Route>

          {/* Strony docelowe z reklam (CMS: "landings", /admin/landingi) — tylko
              adresy aktywnych wpisów; reszta trafia do NotFound. Statyczne trasy
              wyżej mają pierwszeństwo przed parametrem. */}
          <Route path="/:landingSlug" element={<LandingRoute />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>

        {!onLanding && <Footer />}
      </div>
    </ToastHost>
  );
}
